import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, fromEvent, merge, timer } from 'rxjs';
import { filter as rxFilter, takeUntil } from 'rxjs/operators';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { TenantState } from 'src/app/store/state/tenant.state';
import { IdleService } from 'src/app/shared/services/idle.service';
import { IssueSyncApi } from './issue-sync.api';
import { ISSUE_DB_PREFIX, IssueSyncDb, issueSyncDbName } from './issue-sync-db';
import { IssueReplica, ScopeStatus, SyncOutcome, SyncOutcomes } from './issue-replica';
import { IssuePendingWrites, POLL_INTERVAL_MS, SettledWrite, WriteConfirmation } from './issue-pending-writes';
import { IssueSyncTabs, issueSyncChannelName, openSyncChannel } from './issue-sync-tabs';
import {
  IssueSyncDelta, IssueSyncRow, IssueSyncScope, emptyScope, scopeKeyOf,
} from './issue-sync-models';

// Drives the local replica: owns the cadences, the per-tenant database and the
// single-writer election. The replica itself is plain TypeScript and testable with
// no DOM; everything Angular-shaped lives here.
//
// The delta poll is deliberately slow. Geofence arrival is found by a location sweep
// on the server, so a faster poll returns the same rows. The caller's own actions do
// not wait for it — they confirm through confirmWrites.

// Drives the leadership lease and the write deadline. Both need a tick far shorter
// than the poll.
const TICK_MS = 3_000;

@Injectable({ providedIn: 'root' })
export class IssueSyncService implements OnDestroy {
  @SelectSnapshot(TenantState.getCurrentTenantExternalId) private externalTenantId!: string;

  private readonly _rows = new BehaviorSubject<IssueSyncRow[]>([]);
  private readonly _deltas = new Subject<IssueSyncDelta>();
  private readonly _statuses = new BehaviorSubject<ScopeStatus[]>([]);
  private readonly _rolledBack = new Subject<SettledWrite[]>();
  private readonly stop$ = new Subject<void>();

  private db: IssueSyncDb<IssueSyncRow> | null = null;
  private tabs: IssueSyncTabs | null = null;
  private replica: IssueReplica | null = null;
  private tenantId = '';
  private workingScope: IssueSyncScope = emptyScope();
  private running = false;
  // Whether the CURRENT tenant's stored rows have reached the consumer. Separate from
  // `running`, which arms the timers once for the app: a tenant switch rebuilds the
  // replica underneath and its rows have to be published again, while the timers stay
  // where they are because each of them re-checks the tenant before it acts.
  private hydrated = false;
  // A focus that found this tab a follower, waiting for the role to come back.
  private pollOnLeadership = false;

  constructor(
    private readonly api: IssueSyncApi,
    private readonly idle: IdleService,
  ) {}

  // The resident rows at load, open Issues only. Emitted once per hydrate — a return
  // visit, a tenant change, a purge — and NOT per delta. Re-emitting the whole set on
  // every apply would make the cost of one changed row track the size of the store,
  // which is the behaviour this feature exists to avoid.
  get rows$(): Observable<IssueSyncRow[]> { return this._rows.asObservable(); }

  // What each delta changed. The query layer patches from this; the cost tracks the
  // delta rather than the store.
  get deltas$(): Observable<IssueSyncDelta> { return this._deltas.asObservable(); }
  get statuses$(): Observable<ScopeStatus[]> { return this._statuses.asObservable(); }
  // Writes that never confirmed and were rolled back. The caller reports the failure.
  get rolledBack$(): Observable<SettledWrite[]> { return this._rolledBack.asObservable(); }

  // Starts syncing a working scope. Widening the view widens the sync; a scope larger
  // than the client will hold is declined and reported rather than part-filled.
  async start(scope: IssueSyncScope = emptyScope()): Promise<SyncOutcome> {
    this.ensureTenant();
    this.workingScope = scope;
    if (!this.hydrated) {
      this.hydrated = true;
      // A wholesale replacement, which is what a consumer needs after a tenant switch:
      // without it the previous tenant's rows stay in the store and this tenant's
      // deltas land on top of them.
      this._rows.next(await this.replica!.hydrate());
      this.publishStatuses();
    }
    if (!this.running) {
      this.running = true;
      this.startTimers();
    }
    const outcome = await this.replica!.sync(scope);
    // Reconcile on load, not only on the slow cadence. A row can leave scope through a
    // group re-stamp, which produces no removal and does not move scopeVersion, so
    // nothing in the delta stream will ever mention it. Until this runs, a stored row
    // the user may no longer see is on screen.
    if (outcome === SyncOutcomes.Synced && this.isWriter()) await this.replica!.reconcile();
    // Rows reached the consumer as deltas while the walk ran; only the status is left.
    this.publishStatuses();
    return outcome;
  }

  // Records an optimistic patch and settles it against the feed. The patched fields
  // survive any delta that arrives mid-flight, so the row cannot un-apply and re-apply.
  beginWrite(
    issueId: string,
    patch: Partial<IssueSyncRow>,
    confirms: WriteConfirmation,
    requiresRemoval = false,
  ): void {
    this.ensureTenant();
    this.replica!.pendingWrites.begin(issueId, patch, confirms, requiresRemoval);
    // The patch changes no stored row, so nothing else would tell the consumer. Emit the
    // patched row directly, which is what makes the caller's own action feel immediate.
    void this.emitPatched(issueId);
  }

  // Whether a write's shadow is still open — the screen renders "waiting" from this.
  isWritePending(issueId: string): boolean {
    return this.replica?.pendingWrites.isPending(issueId) ?? false;
  }

  async confirmWrites(issueIds: string[]): Promise<void> {
    this.ensureTenant();
    if (!this.isWriter()) {
      // Only one tab fetches. Asking the writer is what keeps a write made here inside
      // its deadline; waiting for the ordinary poll would blow through it and report a
      // failure for a write that landed.
      this.tabs?.requestConfirm(issueIds);
      return;
    }
    await this.replica!.confirmWrites(issueIds);
    this.publishStatuses();
  }

  async reconcile(): Promise<void> {
    this.ensureTenant();
    if (!this.isWriter()) return;
    await this.replica!.reconcile();
    this.publishStatuses();
  }

  // Drops every Issue this browser holds, for every tenant it holds them for. A sign-out
  // needs this: the databases are named by tenant, not by user, so anything left behind is
  // handed to whoever signs in next.
  //
  // Deleting the databases outright rather than only clearing the live replica's stores. A
  // session that never opened an Issues screen has no replica, and a session that visited
  // two tenants has a database for each — in both cases clearing the current one leaves
  // rows on disk.
  async purge(): Promise<void> {
    if (this.replica) {
      await this.replica.purge();
      this._rows.next([]);
      this._statuses.next([]);
    }
    await this.dropIssueDatabases();
  }

  private async dropIssueDatabases(): Promise<void> {
    if (typeof indexedDB === 'undefined') return;
    // `databases()` is the only way to reach a tenant this session never opened. Where it
    // is missing, the tenant in hand is still worth dropping.
    const names = typeof indexedDB.databases === 'function'
      ? (await indexedDB.databases().catch(() => [])).map(info => info.name ?? '')
      : [issueSyncDbName(this.tenantId ?? '')];
    await Promise.all(
      names.filter(name => name.startsWith(ISSUE_DB_PREFIX)).map(name => new Promise<void>(resolve => {
        const request = indexedDB.deleteDatabase(name);
        request.onsuccess = () => resolve();
        request.onerror = () => resolve();
        // Another tab still holds a connection. It completes when that tab lets go;
        // nothing here waits for it.
        request.onblocked = () => resolve();
      })),
    );
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.replica?.stop();
    this.tabs?.close();
    this.db?.close();
  }

  // Only the elected tab fetches and writes. Two tabs advancing the same cursor would
  // each consume pages the other never sees, and neither would report an error.
  private isWriter(): boolean {
    return !this.tabs || this.tabs.isLeader();
  }

  // Rebuilds against the current tenant. The database name and the channel both carry
  // the tenant, so a switch partitions rather than mixing two tenants' rows — which
  // matters because some tenant changes come from a route guard that runs no teardown.
  private ensureTenant(): void {
    const tenantId = this.externalTenantId ?? '';
    if (this.replica && tenantId === this.tenantId) return;

    // Stopping comes first. A walk between pages addresses the feed by whichever tenant
    // is current, so one that keeps running past this point would fetch the incoming
    // tenant's rows and announce them as the outgoing tenant's.
    this.replica?.stop();
    this.tabs?.close();
    this.db?.close();
    // A focus that was still waiting for the role belongs to the tenant being left.
    this.pollOnLeadership = false;
    // The replica below is a different tenant's, so whatever the consumer holds now
    // describes the tenant being left.
    this.hydrated = false;
    this.tenantId = tenantId;
    this.db = new IssueSyncDb<IssueSyncRow>(issueSyncDbName(tenantId));
    this.tabs = new IssueSyncTabs(openSyncChannel(issueSyncChannelName(tenantId)));
    this.tabs.onApplied(applied => { void this.adoptApplied(applied.rowIds, applied.removedIds); });
    this.tabs.onConfirmRequested(issueIds => { void this.confirmWrites(issueIds); });
    this.replica = new IssueReplica(
      this.db,
      this.api,
      new IssuePendingWrites(),
      {
        onScopeChanged: (scopeKey, rowIds, removedIds) =>
          this.tabs?.announceApplied(scopeKey, rowIds, removedIds),
        onRowsApplied: (upserted, removedIds) =>
          this.emitDelta(upserted, removedIds),
        onWriteRolledBack: writes => this._rolledBack.next(writes),
        onStatus: () => this.publishStatuses(),
      },
    );
    this._rows.next([]);
    this._statuses.next([]);
  }

  private startTimers(): void {
    timer(TICK_MS, TICK_MS).pipe(takeUntil(this.stop$)).subscribe(() => {
      // A tab that has stopped fetching must not keep the lease. Only the elected tab polls
      // and reconciles, and idleness is per tab, so a leader left in the background would
      // hold the lease while the tab the user is actually working in — a follower — waits
      // for deltas nobody is fetching. Standing down lets that tab claim it.
      //
      // Only where there is somebody to hand off to. A tab with no channel is alone and
      // cannot claim the role back, so resigning there stops sync for good.
      if (this.tabs?.isLeader() && this.tabs.canHandOff() && this.idle.checkIsIdle()) {
        this.tabs.resign();
      }
      this.tabs?.tick();
      // A reader came back to a tab that had handed the role away. This is the tick that
      // gives it back, so it is also the moment to fetch what was missed.
      if (this.pollOnLeadership && this.isWriter()) {
        this.pollOnLeadership = false;
        void this.poll();
      }
      void this.replica?.expireWrites();
    });

    // checkIsIdle only reads the flag; arming detection is a component's job and doing it
    // here would stack listeners on every consumer.
    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
      rxFilter(() => this.isWriter() && !this.idle.checkIsIdle()),
      takeUntil(this.stop$),
    ).subscribe(() => { void this.poll(); });

    // Returning to the tab is the one moment a user expects current data, and it is
    // also when a lease may have just expired elsewhere.
    merge(fromEvent(window, 'focus'), fromEvent(document, 'visibilitychange')).pipe(
      rxFilter(() => document.visibilityState === 'visible'),
      takeUntil(this.stop$),
    ).subscribe(() => {
      if (this.isWriter()) { void this.poll(); return; }
      // Not the writer: this tab handed the role off while it was away. Claiming it back is
      // the election's job and takes a tick to answer, so asking here would always find a
      // follower. The ask is left standing for whichever tick gains the role.
      //
      // Ticking here as well would be worse than useless: two events arrive together on a
      // tab switch, and two ticks in a row resolve a claim without waiting for the live
      // leader's answer — which is the one thing the handshake is for.
      this.pollOnLeadership = true;
    });
  }

  // Applies the caller's own optimistic patch as a delta, so the query layer sees it
  // without anyone re-reading the store.
  private emitDelta(upserted: IssueSyncRow[], removedIds: string[]): void {
    if (!upserted.length && !removedIds.length) return;
    const patched = upserted.map(row => this.replica!.pendingWrites.patched(row));
    this._deltas.next({ upserted: patched, removedIds });
    this.publishStatuses();
  }

  // Adopts what the writing tab applied. Only this tab's own writer reports rows
  // directly, so a follower reads back exactly the ids it was told about — a cost that
  // tracks the delta, not the store.
  private async adoptApplied(rowIds: string[], removedIds: string[]): Promise<void> {
    if (!this.replica || !this.db) return;
    await this.replica.settleApplied(rowIds, removedIds);
    const envelopes = await this.db.getRows(rowIds);
    this.emitDelta(envelopes.map(envelope => envelope.row), removedIds);
  }

  private async poll(): Promise<void> {
    // The tenant can change under a running timer, and some tenant changes come from a
    // route guard that runs no teardown. Without this the poll would fetch the new
    // tenant's rows — the URL is built from ambient tenant state — and write them into
    // the previous tenant's database.
    this.ensureTenant();
    if (!this.replica) return;
    await this.replica.sync(this.workingScope);
    if (this.replica.needsReconcile()) await this.replica.reconcile();
    this.publishStatuses();
  }

  // Re-emits one row with its pending patch applied, for the caller's own action.
  private async emitPatched(issueId: string): Promise<void> {
    if (!this.db || !this.replica) return;
    const [envelope] = await this.db.getRows([issueId]);
    if (!envelope) return;
    this._deltas.next({
      upserted: [this.replica.pendingWrites.patched(envelope.row)],
      removedIds: [],
    });
  }

  private publishStatuses(): void {
    if (!this.replica) return;
    const keys = new Set([scopeKeyOf(this.workingScope)]);
    const statuses: ScopeStatus[] = [];
    for (const key of keys) {
      const status = this.replica.statusFor(key);
      if (status) statuses.push(status);
    }
    this._statuses.next(statuses);
  }
}
